import {
  useCallback, useEffect, useMemo, useRef, useState,
} from 'react';
import BookingService from '../../Services/BookingService';

// Chat-message prefixes shared with the Python side. Mirror
// `INGEST_ALL_CONFIRMED_PREFIX` and the workflow-paragraph wording in
// the chat-agent's `tools/_offer_ingestion_utils.py` and
// `system_prompt.py`. Keep these in sync across the three components.
const INGEST_PAGE_KEY_PREFIX = 'INGEST_PAGE_KEY:';
const INGEST_ALL_CONFIRMED_PREFIX = 'INGEST_ALL_CONFIRMED:';

const mintPageKey = () => {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  // Defensive — old browsers. The backend treats `page_key` as an
  // opaque string anyway.
  return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
};

/**
 * Encapsulates the offer→bookings agentic workflow state +
 * side-effects, lifted out of the (now-deleted) IngestWorkflow page
 * so the same logic runs inside the ChatWidget's ingest overlay.
 *
 * Inputs are the chat handle from `useChatAgent` plus the current
 * `activeOfferId` from the IngestController. When `activeOfferId`
 * is null this hook is inert (no fetches, no kickoff).
 *
 * The contract for ChatWidget integration:
 *   1. On entering ingest (activeOfferId flips truthy), we reset the
 *      chat session so the workflow has a clean slate.
 *   2. Once status=connected + idle, send the INGEST_PAGE_KEY kickoff
 *      message with the offer id.
 *   3. The agent emits a `bookings-proposal` fenced block → reducer
 *      pushes a `kind: 'proposal'` block (typed SSE OR client-side
 *      fallback parser).
 *   4. `editedRows` seeds from the latest proposal block; reseed on
 *      every fresh block (revise flow).
 *   5. Ingest all → synthesize a chat message with the page_key +
 *      fenced `bookings-final` payload; agent calls commit_bookings.
 */
export default function useIngestSession({
  activeOfferId,
  blocks,
  sessionId,
  status,
  pending,
  send,
  reset,
  scrollToLatest,
}) {
  const active = Boolean(activeOfferId);

  // Page-mint UUID — stable for the lifetime of one ingest session.
  // We remint whenever the offer changes (the old key is meaningless
  // for the new offer's per-row idempotency).
  const pageKeyRef = useRef(null);
  const pageKeyOfferRef = useRef(null);
  if (active && pageKeyOfferRef.current !== activeOfferId) {
    pageKeyRef.current = mintPageKey();
    pageKeyOfferRef.current = activeOfferId;
  }

  // ── Session lifecycle: reset on first activation per offer ──
  //
  // Architecture decision (locked): every ingest starts with a fresh
  // session. Two refs work in tandem:
  //   - `resetForOfferRef`: which offer's reset we already fired (so
  //     StrictMode double-invocation doesn't re-fire it).
  //   - `preResetSessionIdRef`: snapshot of the sessionId at the
  //     moment reset() was called, so the kickoff effect can wait
  //     until React commits the new sessionId before firing.
  const resetForOfferRef = useRef(null);
  const preResetSessionIdRef = useRef(null);
  useEffect(() => {
    if (!active) return;
    if (resetForOfferRef.current === activeOfferId) return;
    resetForOfferRef.current = activeOfferId;
    preResetSessionIdRef.current = sessionId;
    reset();
  }, [active, activeOfferId, reset, sessionId]);

  // ── Kickoff message ──
  //
  // Sends `INGEST_PAGE_KEY:<uuid>\n\nPlease draft bookings from
  // service offer <id>.` exactly once per offer, AFTER reset() has
  // committed (we detect this by observing `sessionId` change away
  // from the snapshot we took in the reset effect). Two reasons the
  // wait matters:
  //   1. send() closes over sessionId; firing in the same effects
  //      pass as reset would route the message to the pre-reset
  //      session, which still has prior conversation memory.
  //   2. `blocks` in the same pass also still contains the
  //      pre-reset history — any "have we kicked off yet?" scan
  //      against blocks would read stale data and false-positive.
  const kickoffOfferRef = useRef(null);
  useEffect(() => {
    if (!active) return;
    if (status !== 'connected') return;
    if (pending) return;
    if (kickoffOfferRef.current === activeOfferId) return;
    // Reset hasn't fired yet for this offer — wait.
    if (resetForOfferRef.current !== activeOfferId) return;
    // Reset fired but React hasn't committed the new sessionId yet
    // — `send` is still the pre-reset closure. Wait one more render.
    if (preResetSessionIdRef.current === sessionId) return;
    kickoffOfferRef.current = activeOfferId;
    send(
      `${INGEST_PAGE_KEY_PREFIX}${pageKeyRef.current}\n\n`
      + `Please draft bookings from service offer ${activeOfferId}. `
      + 'Read the offer first, then emit your proposal as a single '
      + '`json bookings-proposal` fenced block.',
    );
  }, [active, activeOfferId, status, pending, sessionId, send]);

  // ── Reset all ingest-local state when the offer changes / ingest ends ──
  useEffect(() => {
    if (active) return;
    // Cleanup: clear the kickoff-fired and reset-fired guards so the
    // next ingest session for any offer starts fresh.
    kickoffOfferRef.current = null;
    resetForOfferRef.current = null;
    preResetSessionIdRef.current = null;
  }, [active]);

  // ── Latest proposal block (last-block-wins) ──
  const latestProposalBlock = useMemo(() => {
    if (!active) return null;
    for (let i = blocks.length - 1; i >= 0; i -= 1) {
      const b = blocks[i];
      if (b && b.kind === 'proposal' && b.proposal) return b;
    }
    return null;
  }, [active, blocks]);

  // ── Latest commit result ──
  const latestCommitResult = useMemo(() => {
    if (!active) return null;
    for (let i = blocks.length - 1; i >= 0; i -= 1) {
      const b = blocks[i];
      if (b && b.kind === 'commit_result' && b.result) return b.result;
    }
    return null;
  }, [active, blocks]);

  // ── Edited rows ──
  //
  // Seeded from the latest proposal, reseeded whenever the proposal
  // block id changes (revise re-emission). Local edits are discarded
  // on reseed by design — the agent's fresh proposal is the new
  // baseline.
  const [editedRows, setEditedRows] = useState([]);
  const seededFromBlockIdRef = useRef(null);
  useEffect(() => {
    if (!active) return;
    if (!latestProposalBlock) return;
    if (latestProposalBlock.id === seededFromBlockIdRef.current) return;
    seededFromBlockIdRef.current = latestProposalBlock.id;
    const rows = Array.isArray(latestProposalBlock.proposal.rows)
      ? latestProposalBlock.proposal.rows
      : [];
    setEditedRows(rows.map((r) => ({ ...r })));
  }, [active, latestProposalBlock]);

  // Drop edited rows when the offer changes / ingest ends.
  useEffect(() => {
    if (active) return;
    setEditedRows([]);
    seededFromBlockIdRef.current = null;
  }, [active]);

  const handleRowEdit = useCallback((idx, patch) => {
    setEditedRows((prev) => prev.map((r, i) => (i === idx ? { ...r, ...patch } : r)));
  }, []);

  // ── Edits summary (for the Discuss-changes diff) ──
  const editsSummary = useMemo(() => {
    if (!latestProposalBlock) return [];
    const original = latestProposalBlock.proposal?.rows || [];
    const diffs = [];
    editedRows.forEach((edited, idx) => {
      const orig = original[idx] || {};
      const changes = [];
      if (edited.full_name !== orig.full_name) {
        changes.push(`customer ${JSON.stringify(orig.full_name)} → ${JSON.stringify(edited.full_name)}`);
      }
      if (edited.date_datetime !== orig.date_datetime) {
        changes.push(`pickup ${orig.date_datetime} → ${edited.date_datetime}`);
      }
      const editedAdults = edited.passengers?.adults;
      const origAdults = orig.passengers?.adults;
      if (editedAdults !== origAdults) {
        changes.push(`passengers ${origAdults} → ${editedAdults}`);
      }
      if (edited.category?.name !== orig.category?.name) {
        changes.push(`vehicle ${JSON.stringify(orig.category?.name)} → ${JSON.stringify(edited.category?.name)}`);
      }
      if (edited.partner_id !== orig.partner_id) {
        changes.push(`partner_id ${orig.partner_id} → ${edited.partner_id}`);
      }
      if (edited.price !== orig.price) {
        changes.push(`price ${orig.price} → ${edited.price}`);
      }
      if (edited.currency !== orig.currency) {
        changes.push(`currency ${orig.currency} → ${edited.currency}`);
      }
      if (changes.length > 0) diffs.push({ idx, changes });
    });
    return diffs;
  }, [editedRows, latestProposalBlock]);

  const hasEdits = editsSummary.length > 0;

  // ── Handlers: Ingest all + Discuss changes ──
  const handleIngestAll = useCallback(() => {
    if (!active) return;
    if (!latestProposalBlock || !editedRows.length) return;
    if (pending) return;
    const rowsJson = JSON.stringify(editedRows, null, 2);
    const message = (
      `${INGEST_ALL_CONFIRMED_PREFIX}${pageKeyRef.current}\n\n`
      + `Use these final edited rows for commit_bookings (offer_id=${activeOfferId}, `
      + `page_key=${pageKeyRef.current}). Call commit_bookings with confirm=true.\n\n`
      + '```json bookings-final\n'
      + `${rowsJson}\n`
      + '```'
    );
    if (typeof scrollToLatest === 'function') scrollToLatest('auto');
    send(message);
  }, [active, latestProposalBlock, editedRows, pending, activeOfferId, send, scrollToLatest]);

  const handleDiscussChanges = useCallback(() => {
    if (!active) return;
    if (!latestProposalBlock || !editedRows.length) return;
    if (pending) return;
    const rowsJson = JSON.stringify(editedRows, null, 2);
    const editLines = hasEdits
      ? editsSummary.map((d) => `- Row ${d.idx}: ${d.changes.join('; ')}`).join('\n')
      : '_No edits yet — please review the draft and suggest improvements._';
    const message = (
      'I made the following edits to your draft. Please look them over '
      + 'and either acknowledge (re-emit a fresh `bookings-proposal` block '
      + 'with my edits applied) or suggest refinements before I ingest.\n\n'
      + `${editLines}\n\n`
      + 'Current rows:\n\n'
      + '```json bookings-current\n'
      + `${rowsJson}\n`
      + '```'
    );
    if (typeof scrollToLatest === 'function') scrollToLatest('auto');
    send(message);
  }, [
    active, latestProposalBlock, editedRows, pending, send,
    editsSummary, hasEdits, scrollToLatest,
  ]);

  // ── Load-time reconciliation: fetch already-ingested bookings ──
  const [alreadyIngestedBookings, setAlreadyIngestedBookings] = useState([]);
  const [reconciliationLoaded, setReconciliationLoaded] = useState(false);
  useEffect(() => {
    if (!active) {
      setAlreadyIngestedBookings([]);
      setReconciliationLoaded(false);
      return undefined;
    }
    let cancelled = false;
    setReconciliationLoaded(false);
    BookingService.listBookingsForOffer(activeOfferId)
      .then((docs) => {
        if (cancelled) return;
        setAlreadyIngestedBookings(Array.isArray(docs) ? docs : []);
        setReconciliationLoaded(true);
      })
      .catch(() => {
        if (cancelled) return;
        // Reconciliation is best-effort — idempotency keys still
        // prevent duplicate inserts.
        setReconciliationLoaded(true);
      });
    return () => {
      cancelled = true;
    };
  }, [active, activeOfferId]);

  return {
    active,
    pageKey: pageKeyRef.current,
    proposal: latestProposalBlock ? latestProposalBlock.proposal : null,
    rows: editedRows,
    onRowEdit: handleRowEdit,
    onIngestAll: handleIngestAll,
    onDiscussChanges: handleDiscussChanges,
    hasEdits,
    commitResult: latestCommitResult,
    fieldWarnings: latestProposalBlock ? latestProposalBlock.fieldWarnings : null,
    alreadyIngestedBookings,
    reconciliationLoaded,
  };
}

// Re-exported for unit tests and so future call sites can avoid magic
// strings drifting between three files.
export { INGEST_PAGE_KEY_PREFIX, INGEST_ALL_CONFIRMED_PREFIX };
